import { get as _get, mergeWith as _mergeWith } from 'lodash';

import Auth, { FetchAPI, ParamsSerializer } from './auth-service';
import ReportsService from './reports-service';
import {
    MtdtMerge,
    IsRealObject,
    ArrayFromString,
    SaveFile,
    SaveFileFromAPI,
    FilterToEnglish,
    GetBTUUID,
    IsEmpty
} from '../libraries/helpers';

// business logic for columns, filter list, key-value pairs in the details view
// with attributes used by those various components.
// it's not an exhaustive list
// non indexed keys are only listed if they need special treatment (e.g. dates, hidden, json order etc)
const defaults = {
    count: {
        variable: 'count',
        sortable: true,
        title: 'Count',
        field: 'count',
        groupable: false,
        indexed: false,
        addon: true,
        order: -2,
        grouping: true,
        hide: true,
        jsonHidden: true,
        sticky: true,
    },
    activity: {
        variable: 'activity',
        sortable: false,
        title: 'Activity',
        field: 'activity',
        groupable: false,
        indexed: false,
        addon: true,
        order: -1,
        grouping: true,
        hide: true,
        jsonHidden: true,
        sticky: true,
    },
    remove: {
        variable: 'remove',
        sortable: false,
        title: ' ',
        field: 'remove',
        groupable: false,
        resizable: false,
        indexed: false,
        addon: true,
        order: 999,
        hide: true,
        jsonHidden: true,
        disableClickEventBubbling: true,
    },
    event_uuid: {
        variable: 'event_uuid',
        field: 'data.bricata.event_uuid',
        hide: true,
        title: 'Event UUID',
        groupable: false,
        type: 'str',
        indexed: true,
        jsonSort: 1111,
        sortable: false,
        gator: 'bricata:event_uuid',
        hint: 'Full alert UUID',
    },
    // once is enough for uuid
    uuid: {
        variable: 'uuid',
        field: 'uuid',
        hide: true,
        title: 'UUID',
        type: 'str',
        groupable: false,
        indexed: false,
        sortable: false,
        jsonHidden: true,
        tableHidden: true,
    },
    // once is enough for uuid
    id: {
        variable: 'id',
        field: 'id',
        hide: true,
        title: 'id',
        type: 'str',
        groupable: false,
        indexed: false,
        sortable: false,
        jsonHidden: true,
        tableHidden: true,
    },
    severity: {
        variable: 'severity',
        field: 'data.alert.severity',
        hide: false,
        title: 'Severity',
        indexed: true,
        jsonSort: 1,
        align: 'center',
        gator: 'alert:severity',
        type: 'number',
        select: [1, 2, 3, 4],
        hint: '1-4',
        order: 1,
    },
    tag: {
        variable: 'tag',
        field: 'data.bricata.tag',
        hide: false,
        title: 'Tag',
        indexed: true,
        jsonHidden: true,
        gator: 'bricata:tag',
        getter: d => _get(d, 'data.tag', []),
        hint: 'Full tag string',
        order: 10,
    },
    mitre_category: {
        variable: 'mitre_category',
        field: 'data.bricata.mitre_category',
        hide: true,
        title: 'ATT&CK Category',
        indexed: true,
        hint: 'Mitre ATT&CK Category ID',
        order: 10,
        gator: 'none',
        type: 'mitre',
    },
    sensor_uuid: {
        variable: 'sensor_uuid',
        field: 'data.bricata.sensor_uuid',
        hide: false,
        title: 'Sensor',
        type: 'sensor_uuid',
        indexed: true,
        gator: 'bricata:sensor_uuid',
        order: 2,
    },
    src_ip: {
        variable: 'src_ip',
        field: 'data.src_ip',
        hide: false,
        title: 'Source IP',
        indexed: true,
        jsonSort: 2,
        type: 'ip',
        order: 3,
    },
    src_port: {
        variable: 'src_port',
        field: 'data.src_port',
        hide: false,
        title: 'Src Port',
        indexed: true,
        jsonSort: 3,
        type: 'port',
        order: 4,
    },
    dest_ip: {
        variable: 'dest_ip',
        field: 'data.dest_ip',
        hide: false,
        title: 'Destination IP',
        type: 'ip',
        indexed: true,
        jsonSort: 2,
        order: 5,
    },
    dest_port: {
        variable: 'dest_port',
        field: 'data.dest_port',
        hide: false,
        title: 'Dest Port',
        type: 'port',
        indexed: true,
        jsonSort: 3,
        order: 6,
    },
    xff: {
        variable: 'xff',
        field: 'data.http.xff',
        hide: true,
        type: 'ip',
        title: 'X-Forwarded-For',
        getter: d => _get(d, 'data.http.xff', '').split(','),
        indexed: true,
        gator: 'http:xff',
        hint: 'Comma separated list of IP or IP:port',
    },
    xorig: {
        variable: 'xorig',
        field: 'data.http.xorig',
        hide: true,
        type: 'ip',
        title: 'X-Orig',
        indexed: true,
        gator: 'http:xorig',
    },
    proto: {
        variable: 'proto',
        field: 'data.proto',
        hide: false,
        title: 'Proto',
        type: 'str',
        getter: d => _get(d, 'data.proto', '').toUpperCase(),
        indexed: true,
        jsonSort: 6,
        hint: 'Protocol, e.g. TCP, UDP',
        order: 7,
    },
    signature: {
        variable: 'signature',
        field: 'data.alert.signature',
        hide: false,
        title: 'Message',
        type: 'str',
        indexed: true,
        jsonHidden: true,
        gator: 'alert:signature',
        hint: 'Full or partial alert message',
        order: 8,
    },
    signature_id: {
        variable: 'signature_id',
        field: 'data.alert.signature_id',
        hide: true,
        title: 'Signature',
        type: 'number',
        indexed: true,
        jsonSort: 1111,
        gator: 'alert:signature_id',
        hint: 'Full signature ID',
    },
    category: {
        variable: 'category',
        field: 'data.alert.category',
        hide: true,
        title: 'Category',
        type: 'str',
        indexed: true,
        gator: 'alert:category',
        hint: 'Full or partial category text',
    },
    netstamp: {
        variable: 'netstamp',
        field: 'data.timestamp',
        hide: true,
        title: 'Net Time',
        type: 'date',
        groupable: false,
        indexed: true,
    },
    timestamp: {
        variable: 'timestamp',
        field: 'timestamp',
        hide: false,
        title: 'Timestamp',
        type: 'date',
        groupable: false,
        indexed: true,
        jsonSort: 7,
        order: 9,
        getter: d => GetBTUUID(d, 'data.orig_ts') === undefined
            ? d.timestamp : _get(d, 'data.orig_ts')
    },
    // only available for bt mode
    db_timestamp: {
        variable: 'db_timestamp',
        field: 'db_timestamp',
        hide: true,
        type: 'date',
        title: 'DB Timestamp',
        sticky: true,
        sortable: true,
        groupable: false,
        columnSelectorHidden: true,
        getter: d => d.timestamp,
    },
    community_id: {
        variable: 'community_id',
        field: 'data.community_id',
        hide: true,
        title: 'Community ID',
        type: 'str',
        indexed: true,
        fivetuple: true,
        hint: '5-tuple hash'
    },
    clisrv_id: {
        variable: 'clisrv_id',
        field: 'data.clisrv_id',
        hide: true,
        title: 'Client-Server ID',
        type: 'str',
        indexed: true,
        threetuple: true,
        hint: 'SrcIP, DestIP, Proto Hash'
    },
    vlan: {
        variable: 'vlan',
        hide: true,
        title: 'VLAN',
        field: 'data.vlan',
        type: 'number',
        indexed: true,
        jsonSort: 1111,
        hint: 'VLAN tag',
    },
    compressed: {
        variable: 'compressed',
        hide: true,
        title: 'Compressed',
        field: 'data.bricata.compressed',
        type: 'bool',
        indexed: false,
        align: 'center',
    },
    action: {
        variable: 'action',
        hide: true,
        title: 'Action',
        field: 'data.alert.action',
        type: 'str',
        indexed: true,
        gator: 'alert:action',
        hint: "'allowed' or 'blocked'",
        select: ['allowed', 'blocked'],
    },
    orig_city: {
        variable: 'orig_city',
        field: 'data.bricata.src_location.city_name',
        title: 'GeoIP Orig City',
        indexed: true,
        jsonSort: 4,
        hide: true,
    },
    orig_country: {
        variable: 'orig_country',
        field: 'data.bricata.src_location.country',
        title: 'GeoIP Orig Country',
        indexed: true,
        jsonSort: 4,
        hide: true,
    },
    orig_state:{
        variable: 'orig_state',
        field: 'data.bricata.src_location.state',
        title: 'GeoIP Orig State',
        indexed: true,
        jsonSort: 4,
        hide: true,
    },
    dest_state:{
        variable: 'dest_state',
        field: 'data.bricata.dest_location.state',
        title: 'GeoIP Dest State',
        indexed: true,
        jsonSort: 4,
        hide: true,
    },
    dest_city: {
        variable: 'dest_city',
        field: 'data.bricata.dest_location.city_name',
        title: 'GeoIP Dest City',
        indexed: true,
        jsonSort: 4,
        hide: true,
    },
    dest_country: {
        variable: 'dest_country',
        field: 'data.bricata.dest_location.country',
        title: 'GeoIP Dest Country',
        indexed: true,
        jsonSort: 4,
        hide: true,
    },
    dest_location: {
        variable: 'dest_location',
        field: 'data.bricata.dest_location.location',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    dest_location_lat: {
        variable: 'dest_location_lat',
        field: 'data.bricata.dest_location.location.lat',
        indexed: false,
        jsonHidden: false,
        hide: true,
        jsonSort: 5,
        title: 'Dest Latitude',
    },
    dest_location_lon: {
        variable: 'dest_location_lon',
        field: 'data.bricata.dest_location.location.lon',
        indexed: false,
        jsonHidden: false,
        hide: true,
        jsonSort: 5,
        title: 'Dest Longitude',
    },
    src_location: {
        variable: 'src_location',
        field: 'data.bricata.src_location.location',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    src_location_lat: {
        variable: 'src_location_lat',
        field: 'data.bricata.src_location.location.lat',
        indexed: false,
        jsonHidden: false,
        hide: true,
        jsonSort: 5,
        title: 'Src Latitude',
    },
    src_location_lon: {
        variable: 'src_location_lon',
        field: 'data.bricata.src_location.location.lon',
        indexed: false,
        jsonHidden: false,
        hide: true,
        jsonSort: 5,
        title: 'Src Longitude',
    },
    app_proto: {
        variable: 'app_proto',
        field: 'data.app_proto',
        indexed: true,
        getter: d => _get(d, 'data.app_proto', '').toUpperCase(),
        jsonSort: 6,
        type: 'str',
        hide: false,
        title: 'App Proto',
        hint: 'App Protocol, e.g. FTP, SMTP,HTTP',
    },
    rev: {
        variable: 'rev',
        field: 'data.alert.rev',
        indexed: false,
        jsonSort: 1111,
        hide: true,
        tableHidden: true,
    },
    flow_uuid: {
        variable: 'flow_uuid',
        title: 'Flow UUID',
        type: 'str',
        field: 'data.bricata.flow_uuid',
        indexed: true,
        jsonSort: 1111,
        hide: true,
        gator: 'bricata:flow_uuid',
        hint: 'Full Flow UUID',
    },
    script_name: {
        variable: 'script_name',
        field: 'data.alert.script_name',
        title: 'Zeek script name',
        indexed: true,
        hide: true,
        gator: 'alert:script_name',
        hint: 'Full Zeek script name',
    },
    event_type: {
        variable: 'event_type',
        field: 'data.event_type',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    packet: {
        variable: 'packet',
        field: 'data.packet',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    payload: {
        variable: 'payload',
        field: 'data.payload',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    read: {
        variable: 'read',
        field: 'data.bricata.read',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    event_format: {
        variable: 'event_format',
        field: 'data.bricata.event_format',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    event_source: {
        variable: 'event_source',
        field: 'data.bricata.event_source',
        indexed: true,
        title: 'Event Source',
        groupable: true,
        type: 'str',
        sortable: true,
        hide: true,
        hint: 'e.g. suricata, ml_server, cylance',
    },
    rx_host: {
        variable: 'rx_host',
        field: 'data.bro.rx_host',
        indexed: false,
        jsonHidden: true,
        hide: true,
        title: 'Rx Host',
    },
    rx_port: {
        variable: 'rx_port',
        field: 'data.bro.rx_port',
        indexed: false,
        jsonHidden: true,
        hide: true,
        title: 'Rx Port',
    },
    tx_host: {
        variable: 'tx_host',
        field: 'data.bro.tx_host',
        indexed: false,
        jsonHidden: true,
        hide: true,
        title: 'Tx Host',
    },
    tx_port: {
        variable: 'tx_port',
        field: 'data.bro.tx_port',
        indexed: false,
        jsonHidden: true,
        hide: true,
        title: 'Tx Port',
    },
    sensor_ipv4: {
        variable: 'sensor_ipv4',
        field: 'data.bricata.sensor_ipv4',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    sensor_ipv6: {
        variable: 'sensor_ipv6',
        field: 'data.bricata.sensor_ipv6',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    rule_options: {
        variable: 'rule_options',
        field: 'rule.options',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    mtdt_uuid: {
        variable: 'mtdt_uuid',
        field: 'mtdt.uuid',
        indexed: false,
        tableHidden: true,
    },
    malware: {
        variable: 'malware',
        field: 'data.cylance',
        indexed: false,
        jsonHidden: true,
        tableHidden: true,
    },
    bt_mode: {
        variable: 'bt_mode',
        field: 'data.bt.mode',
        indexed: false,
        tableHidden: true,
        jsonHidden: true,
        title: 'BT Mode'
    },
    bt_external_pcaps: {
        variable: 'bt_external_pcaps',
        field: 'data.bt.external_pcaps',
        indexed: false,
        tableHidden: true,
        jsonHidden: true,
        title: 'BT External Pcaps'
    },
    bt_internal_pcaps: {
        variable: 'bt_internal_pcaps',
        field: 'data.bt.internal_pcaps',
        indexed: false,
        tableHidden: true,
        jsonHidden: true,
        title: 'BT Internal Pcaps'
    },
    backtesting: {
        variable: 'backtesting',
        field: 'data.bt',
        indexed: true,
        title: 'backtesting',
        hide: true,
        tableHidden: true,
        gator: 'bt.mode',
        type: 'bool',
        getter: d => _get(d, 'data.bt') !== undefined,
        columnSelectorHidden: true,
    },
    bt_pcap_name: {
        variable: 'bt_pcap_name',
        field: 'data.bricata.bt_pcap_name',
        indexed: false,
        title: 'BT PCAP Name',
        hide: true,
        tableHidden: true,
        columnSelectorHidden: true,
    },
    job_uuid: {
        variable: 'job_uuid',
        field: 'data.bt_uuid',
        indexed: true,
        title: 'Backtesting Job',
        hide: true,
        sortable: true,
        groupable: true,
        gator: 'bt_uuid',
        getter: d => _get(d, 'data.bt_uuid'),
        type: 'bt_job_uuid',
    },
    // hide obsolete bt_uuid field
    old_job_uuid: {
        variable: 'old_job_uuid',
        field: 'data.bricata.bt_uuid',
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    },
    tunnel_depth: {
        variable: 'tunnel_depth',
        field: 'data.tunnel.depth',
        indexed: true,
        title: 'Tunnel Depth',
        info: 1,
        hide: true,
    },
    tunnel_proto: {
        variable: 'tunnel_proto',
        field: 'data.tunnel.proto',
        indexed: true,
        title: 'Tunnel Proto',
        info: 1,
        hide: true,
    },
    tunnel_src_ip: {
        variable: 'tunnel_src_ip',
        field: 'data.tunnel.src_ip',
        indexed: true,
        title: 'Tunnel Src IP',
        type: 'ip',
        info: 1,
        hide: true,
    },
    tunnel_dest_ip: {
        variable: 'tunnel_dest_ip',
        field: 'data.tunnel.dest_ip',
        indexed: true,
        title: 'Tunnel Dest IP',
        type: 'ip',
        info: 1,
        hide: true,
    },
    tunnel_src_port: {
        variable: 'tunnel_src_port',
        field: 'data.tunnel.src_port',
        indexed: true,
        title: 'Tunnel Src Port',
        info: 1,
        hide: true,
        type: 'port',
    },
    tunnel_dest_port: {
        variable: 'tunnel_dest_port',
        field: 'data.tunnel.dest_port',
        indexed: true,
        title: 'Tunnel Dest Port',
        info: 1,
        hide: true,
        type: 'port',
    },
    SHA256: {
        variable: 'SHA256',
        field: 'data.bro.sha256',
        indexed: false,
        title: 'SHA256',
        type: 'file_hash',
        hide: true,
        tableHidden: true,
    },
    SHA1: {
        variable: 'SHA1',
        field: 'data.bro.sha1',
        indexed: false,
        title: 'SHA1',
        type: 'file_hash',
        hide: true,
    },
    MD5: {
        variable: 'MD5',
        field: 'data.bro.md5',
        indexed: false,
        hide: true,
        title: 'MD5',
        type: 'file_hash',
    },
    backtesting_uuid: {
        variable: 'backtesting_uuid',
        title: 'Backtesting Job',
        field: 'backtesting.uuid',
        indexed: false,
        hide: true,
        tableHidden: true,
    },
    backtesting_pcapdirs: {
        variable: 'backtesting_pcapdirs',
        title: 'PCAP Dirs',
        field: 'backtesting.data.pcapdirs',
        indexed: false,
        hide: true,
        tableHidden: true,
    },
    backtesting_created: {
        variable: 'backtesting_created',
        field: 'backtesting.created',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    backtesting_updated: {
        variable: 'backtesting_updated',
        field: 'backtesting.updated',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    backtesting_queued: {
        variable: 'backtesting_queued',
        field: 'backtesting.queued',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    backtesting_pushed: {
        variable: 'backtesting_pushed',
        field: 'backtesting.pushed',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    backtesting_completed: {
        variable: 'backtesting_completed',
        field: 'backtesting.completed',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    backtesting_start: {
        variable: 'backtesting_start',
        field: 'backtesting.data.start',
        title: 'Data Range Start',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    backtesting_end: {
        variable: 'backtesting_end',
        field: 'backtesting.data.end',
        title: 'Data Range End',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    backtesting_filter_start: {
        variable: 'backtesting_filter_start',
        field: 'backtesting.data.timefilter_start',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    backtesting_filter_end: {
        variable: 'backtesting_filter_end',
        field: 'backtesting.data.timefilter_end',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    backtesting_filter_enabled: {
        variable: 'backtesting_filter_enabled',
        field: 'backtesting.data.timefilter_enabled',
        indexed: false,
        hide: true,
        type: 'bool',
        tableHidden: true,
        getter: d => d,
    },
    backtesting_timestamp: {
        variable: 'backtesting_timestamp',
        field: 'backtesting.data.timestamp',
        indexed: false,
        hide: true,
        type: 'date',
        tableHidden: true,
    },
    // entry for json fields in detail view
    orig_ts: {
        variable: 'orig_ts',
        field: 'backtesting.data.orig_ts',
        hide: true,
        title: 'Original Timestamp',
        type: 'date',
        indexed: false,
        tableHidden: true,
    },
    // redundant
    backtesting_job_uuid: {
        variable: 'backtesting_job_uuid',
        field: 'backtesting.data.job_uuid',
        indexed: false,
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    },
    backtesting_job_name: {
        variable: 'backtesting_job_name',
        field: 'backtesting.data.name',
        indexed: false,
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    },
    backtesting_job_desc: {
        variable: 'backtesting_job_desc',
        field: 'backtesting.data.description',
        indexed: false,
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    },
    backtesting_job_enabled: {
        variable: 'backtesting_job_enabled',
        field: 'backtesting.data.enabled',
        indexed: false,
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    },
    backtesting_last_action_ts: {
        variable: 'backtesting_last_action_ts',
        field: 'backtesting.last_action_ts',
        indexed: false,
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    },
    // hide bt fields until we can figure out what can safety be removed
    backtesting_bt_mode: {
        variable: 'backtesting_bt_mode',
        field: 'backtesting.data.mode',
        indexed: false,
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    },
    backtesting_duration: {
        variable: 'backtesting_duration',
        field: 'backtesting.duration',
        indexed: false,
        hide: true,
        tableHidden: true,
        jsonHidden: true,
    }
};

const DefaultWidths = {
    count: 100,
    activity: 300,
    remove: 50,
    src_ip: 200,
    dest_ip: 200,
    timestamp: 160,
    netstamp: 160,
    signature: 280,
    mitre_category: 200,
};

const DefaultMapping = {
    src_ip: ['src_ip'],
    src_port: ['src_port'],
    dest_ip: ['dest_ip'],
    dest_port: ['dest_port'],
};

// the columns in this list needs to be 'indexed'
const AllMapping = {
    src_ip: ['src_ip', 'xff', 'xorig', 'tunnel_src_ip'],
    src_port: ['src_port', 'tunnel_src_port'],
    dest_ip: ['dest_ip', 'tunnel_dest_ip'],
    dest_port: ['dest_port', 'tunnel_dest_port'],
};

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';
const prefix = (cmc) => (MC2CHECK && cmc ? `mc2/cmcapi/${cmc.name}/` : '');

class AlertsDataService {
    constructor() {
        // we only need one of these
        if (!AlertsDataService.instance) {
            AlertsDataService.instance = this;
        }
        this.searchPlaceholder = 'search alerts';

        this.columnsMap = defaults;

        // fields is an ordered list of columns with visible columns up front
        this.fields = Object.values(defaults)
            .sort((a, b) => {
                if (a.hide && !b.hide) return 1;
                if (b.hide && !a.hide) return -1;
                if (a.order === undefined && b.order === undefined) {
                    return a.variable.localeCompare(b.variable);
                }
                return (a.order || 0) - (b.order || 0);
            });
        // fieldsMap is a map keyed by field - which is how the table does column lookups
        this.fieldsMap = Object.values(defaults)
            .reduce((a, c) => ({ ...a, [c.field]: c }), {});

        // cached data for alert details
        this.metadata = {};
        this.dpid = {};
        this.savedFilters = [];

        this.isMapped = v => {
            return this.columnMapping[v] &&
                (this.columnMapping[v].length > 1 || this.columnMapping[v][0] !== v);
        };

        // cached column mappings
        this.columnMapping = DefaultMapping;
        this.doColumnMapping = mapping => {
            this.columnMapping = { ...DefaultMapping, ...mapping };
            return this.columnMapping;
        };
        this.groupMapping = groups => {
            if (!groups || !groups.length) return undefined;

            return groups
                .map(g => (typeof g === 'string') ? g : g.variable)
                .map(g => this.columnMapping[g] ? this.columnMapping[g][0] : g);
        };
        this.sortMapping = sort => {
            const ss = (sort || '').split(' ');
            if (ss.length < 2) return;
            const m = this.columnMapping[ss[0]];
            return m ? m.join(` ${ss[1]},`) + ` ${ss[1]}` : sort;
        };
        this.demap = f => {
            if (!f || f.disabled) return f;

            if (f.nodes) {
                f.nodes = f.nodes.map(this.demap);
            }
            if (f.variable && this.columnMapping[f.variable]) {
                f = {
                    operator: 'Or',
                    nodes: this.columnMapping[f.variable].map(variable => (
                        { ...f, variable }
                    ))
                };
            }
            return f;
        };
        // before sending filter we need to process:
        // 1. sanitize values
        // 2. demap variables
        // 3. json stringify
        // 4. url encode
        this.processFilter = filter => {
            if (!filter) return undefined;
            if (filter === '') return filter;

            // deep copy so the GUI isn't impacted
            const f = JSON.parse(JSON.stringify(filter));
            return encodeURIComponent(
                JSON.stringify(
                    this.demap(
                        this.sanitize(f)
                    )
                )
            );
        };

        // start data access
        this.getData = ({
            sort,
            search,
            filter,
            start,
            end,
            groups = [],
            tagFilters = [],
            tagsOp = 'Or',
            read = 'all',
            backtesting,
            selectedBTJobs,
            jobCreated,
            page = 1,
            pageSize = 50,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const p = {
                sort: this.sortMapping(sort),
                limit: 50,
                offset: (page - 1) * pageSize,
                json_filter: this.processFilter(filter),
                group: this.groupMapping(groups),
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                search,
                read,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                start_time: start,
                end_time: end,
            };
            if (page < 2) {
                this.controller && this.controller.abort();
                this.controller = new AbortController();
            }
            // let's put the filter in the request body
            return FetchAPI({
                method: 'POST',
                url: `${prefix(cmc)}alerts/?${ParamsSerializer(p)}`,
                body: {
                    json_filter: filter
                        ? this.processFilter(filter)
                        : undefined
                },
                signal: this.controller ? this.controller.signal : undefined
            }).then(res => {
                if (!res) res = { objects: [] };

                // Log GeoIP data structure from backend for debugging
                if (res.objects && res.objects.length > 0 && res.objects[0].data) {
                    console.log('[GeoIP Debug] Backend Alert Data:', {
                        alert_uuid: res.objects[0].uuid,
                        src_location: res.objects[0].data.bricata?.src_location,
                        dest_location: res.objects[0].data.bricata?.dest_location,
                    });
                }

                // if grouping, add a group key to each row
                if (groups.length > 0) {
                    (res.objects || []).forEach(a => {
                        a.groups = groups.map(g => ({ key: g.variable, value: _get(a, g.field) }), []);
                        a.key = _groupKey(a);
                    });
                }
                return {
                    data: res.objects || [],
                    asof: res.timestamp,
                    controller: this.controller
                };
            });
        };

        this.getAlert = (uuid, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({ method: 'GET', url: `${prefix(cmc)}alert/${encodeURIComponent(uuid)}` });
        };

        this.getSelectAlerts = ({ start, sort, uuids, cmc }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const p = {
                sort: sort && sort.startsWith('remove') ? undefined : this.sortMapping(sort),
                start_time: start,
            };
            // this guy and getData will cancel eachother
            this.controller && this.controller.abort();
            this.controller = new AbortController();

            return FetchAPI({
                method: 'PUT',
                url: `${prefix(cmc)}alerts/uuids/?${ParamsSerializer(p)}`,
                body: uuids || [],
                signal: this.controller.signal,
            }).then(res => {
                // use remove column to hide a sort order - we want to retain viewed order
                return {
                    data: (res || []).map((a, i) => ({ ...a, remove: i })),
                    page: 0,
                    totalCount: (res || []).length,
                };
            });
        };

        this.doReadAlert = (uuid, read, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            FetchAPI({ method: 'PUT', url: `${prefix(cmc)}alerts/${encodeURIComponent(uuid)}/${read ? 'read' : 'unread'}` });
        };

        // https://cmc/api/alerts/meta/874b6f03-46ce-81e6-f098-aab3bcc62223/2020-05-11T17%3A53%3A15.402554Z
        this.getMetadata = (uuid, timestamp, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return this.metadata[uuid] || this.dpid[uuid]
                ? Promise.resolve({conn: this.metadata[uuid] || {}, dpid: this.dpid[uuid] || {}})
                : FetchAPI({ method: 'GET', url: `${prefix(cmc)}alerts/meta/${encodeURIComponent(uuid)}/${encodeURIComponent(timestamp)}` })
                    .then(res => {
                        // don't cache empty results
                        if (!res || IsEmpty(res)) return;

                        const conn = res.conn || {};
                        const dpid = res.dpid || {};
                        
                        this.metadata[uuid] = conn;
                        this.dpid[uuid] = dpid;
                        return {conn, dpid};
                    });
        };

        this.getSandboxReport = (uuid, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({ method: 'GET', url: `${prefix(cmc)}alerts/sandbox/${encodeURIComponent(uuid)}` });
        };

        this.downloadSandboxReport = ({ alert, task, sensor, type, url, cmc }) => {
            if (MC2CHECK && !this.prefix) return;
            const auth = Auth.getAuth();
            const t = auth ? auth.token : '';
            SaveFileFromAPI(
                `/api/${prefix(cmc)}alerts/sandbox/remote/${encodeURIComponent(alert)}/${encodeURIComponent(task)}/`,
                { sensor, type, url, t }
            );
        };

        this.downloadMalwareFile = ({ uuid, sensor, sha256, md5, cmc }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'POST',
                url: `${prefix(cmc)}alerts/malware`,
                body: { sensor_uuid: sensor, sha256, md5, filename: uuid },
                responseType: 'arraybuffer',
            }).then(buffer => {
                if (buffer.byteLength) {
                    SaveFile(buffer, uuid + '.zip');
                }
            },
                reject => {
                    console.error('error downloading malware file: ', reject);
                }
            );
        };

        this.getTimeline = ({
            search,
            filter,
            start,
            end,
            groups = [],
            tagFilters = [],
            tagsOp = 'Or',
            backtesting,
            selectedBTJobs,
            jobCreated,
            controller,
            interval,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                search,
                start_time: start,
                end_time: end,
                group: this.groupMapping(groups),
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
                interval,
            };
            return FetchAPI({
                method: 'POST',
                url: `${prefix(cmc)}alerts/timeline/?${ParamsSerializer(params)}`,
                body: {
                    json_filter: filter
                        ? this.processFilter(filter)
                        : undefined
                },
                signal: controller ? controller.signal : undefined
            }).then(res => {
                res = res || { objects: [] };
                res.objects.sort((a, b) => AlertsService.timestamp(a).getTime() - AlertsService.timestamp(b).getTime());
                return res;
            });
        };

        this.getGeoHistory = ({
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            read = 'all',
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                search,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                read,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            this.controller && this.controller.abort();
            this.controller = new AbortController();
            return FetchAPI({
                method: 'POST',
                url: `${prefix(cmc)}alerts/geo/history/?${ParamsSerializer(params)}`,
                body: {
                    json_filter: filter
                        ? this.processFilter(filter)
                        : undefined
                },
                signal: this.controller.signal,
            }).then(res => {
                return { ...res, controller: this.controller };
            });
        };

        this.startGeofeed = ({
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            read = 'all',
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }, callback, heartbeatCallback, errorback) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                search,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                read,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            // SSE not supported, but we don't have to keep saying it
            if (typeof (EventSource) === 'undefined') {
                if (!this.warned) {
                    this.warned = true;
                    console.log('Streaming is not supported on this browser.');
                }
                return errorback(new Error('not supported'));
            }
            // already streaming
            if (this.dataStream) return errorback(new Error('already streaming'));

            const auth = Auth.getAuth();
            const t = auth ? auth.token : '';
            const stream = ({ start }) => {
                const p = { ...params, start_time: start, t };
                const prefix = (cmc) => (MC2CHECK && cmc ? `/mc2/cmcapi/stream/${cmc.name}/` : '');
                return new EventSource(`/api/${prefix(cmc)}alerts/geo/stream/?${ParamsSerializer(p)}`);
            };

            let attempt = 0;
            this.dataStream = stream({ start });
            this.dataStream.onopen = () => { };
            this.dataStream.onmessage = (e) => {
                attempt = 0;
                let d;
                try {
                    d = JSON.parse(e.data);
                } catch (err) {
                    console.error('dataStream', 'onmessage', 'json error', err, e.data);
                }
                start = d.data.end;
                if (this.dataStream && d.data) {
                    callback(d.data);
                }
            };
            this.dataStream.addEventListener('start', (e) => { console.debug('dataStream start', e) });
            this.dataStream.addEventListener('heartbeat', (e) => {
                console.debug('dataStream heartbeat', e);
                heartbeatCallback();
            });
            this.dataStream.addEventListener('done', (e) => {
                console.debug('dataStream done');
                this.dataStream.close();
                this.dataStream = undefined;
            });
            this.dataStream.onerror = (e) => {
                console.error('dataStream', 'onerror', e);
                // readyState: CONNECTING (0), OPEN (1), CLOSED (2)
                if (this.dataStream.readyState === 2 && attempt < 5) {
                    // FireFox will close the connection after only one reconnection attempt
                    attempt++;
                    setTimeout(() => {
                        this.dataStream = stream({ start });
                    }, 2000);
                } else if (this.dataStream.readyState === 2) {
                    return errorback(new Error("Browser won't keep connection open"));
                }
            };
        };

        this.stopGeofeed = () => {
            this.dataStream && this.dataStream.close();
            this.dataStream = undefined;
        };

        this.aggregate = (data) => { return _aggregate(data) };

        this.filterFromGroups = (alert, groups = []) => {
            return {
                operator: 'And',
                nodes: groups.reduce((a, c) => {
                    const value = _get(alert, c.field) === undefined ? '' : _get(alert, c.field);
                    return !Array.isArray(value)
                        ? [...a, ({ variable: c.variable, value: `${value}` })]
                        : [...a, ...value.map(v => ({ variable: c.variable, value: `${v}` }))];
                }, [])
            };
        };

        this.cacheSavedFilters = () => {
            return FetchAPI({ method: 'GET', url: 'named_storage/alerts/' }).then(res => {
                this.savedFilters = res || [];
                return this.savedFilters;
            });
        };

        this.deleteSavedFilter = uuid => {
            return FetchAPI({
                method: 'DELETE',
                url: `named_storage/alerts/${encodeURIComponent(uuid)}/`
            }).then(this.cacheSavedFilters);
        };

        this.upsertSavedFilter = filter => {
            return FetchAPI({ method: 'PUT', url: 'named_storage/alerts/', body: filter })
                .then(this.cacheSavedFilters);
        };

        this.tagAlert = (tag, alert, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            if (!alert || !tag) return;
            return FetchAPI({ method: 'PUT', url: `${prefix(cmc)}alerts/${encodeURIComponent(alert.uuid)}/tag/${tag}/` });
        };

        this.untagAlert = (tag, alert, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            if (!alert || !tag) return;
            return FetchAPI({ method: 'DELETE', url: `${prefix(cmc)}alerts/${encodeURIComponent(alert.uuid)}/tag/${tag}/` });
        };

        this.tagAlerts = (tag, {
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            read,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                search,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                read,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            return FetchAPI({
                method: 'PUT',
                url: `${prefix(cmc)}alerts/tags/${tag}/?${ParamsSerializer(params)}`,
                body: {
                    json_filter: filter
                        ? this.processFilter(filter)
                        : undefined
                },
            });
        };

        this.untagAlerts = (tag, {
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            read,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                search,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                read,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            return FetchAPI({
                method: 'DELETE',
                url: `${prefix(cmc)}alerts/tags/${tag}/?${ParamsSerializer(params)}`,
                body: {
                    json_filter: filter
                        ? this.processFilter(filter)
                        : undefined
                },
            });
        };

        this.setAlertMitre = (alertuuid, mitre, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'PUT',
                url: `${prefix(cmc)}alerts/${alertuuid}/mitre/${mitre}/`,
            });
        };

        this.clearAlertMitre = (alertuuid, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            return FetchAPI({
                method: 'DELETE',
                url: `${prefix(cmc)}alerts/${alertuuid}/mitre`,
            });
        };

        this.setAlertsMitre = (mitre, {
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            read,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                search,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                read,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            return FetchAPI({
                method: 'PUT',
                url: `${prefix(cmc)}alerts/mitre/${mitre.id}/?${ParamsSerializer(params)}`,
                body: {
                    json_filter: filter
                        ? this.processFilter(filter)
                        : undefined
                },
            });
        };

        this.clearAlertsMitre = ({
            search,
            filter,
            start,
            end,
            tagFilters = [],
            tagsOp = 'Or',
            read,
            backtesting,
            selectedBTJobs,
            jobCreated,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const params = {
                json_filter: this.processFilter(filter),
                search,
                start_time: start,
                end_time: end,
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                read,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                job_created: jobCreated,
            };
            return FetchAPI({
                method: 'DELETE',
                url: `${prefix(cmc)}alerts/mitre/?${ParamsSerializer(params)}`,
                body: {
                    json_filter: filter
                        ? this.processFilter(filter)
                        : undefined
                }
            });
        };

        this.saveNote = (uuid, note, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            // https://cmc/api/alert/46154172-4681-b7cf-b1a3-57821cfe2e8e/_savenote
            return FetchAPI({ method: 'PUT', url: `${prefix(cmc)}alert/${encodeURIComponent(uuid)}/_savenote`, body: { note } });
        };

        this.generateReport = ({
            sort,
            search,
            filter,
            start,
            end,
            groups = [],
            tagFilters = [],
            tagsOp = 'Or',
            read = 'all',
            backtesting,
            selectedBTJobs,
            jobCreated,
            columns,
            format,
            cmc,
        }) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const p = {
                sort: this.sortMapping(sort),
                json_filter: this.processFilter(filter),
                group: this.groupMapping(groups),
                tags: tagFilters.length > 0 ? tagFilters : undefined,
                tags_op: tagsOp,
                search,
                read,
                bt: backtesting > 0 ? backtesting === 1 : undefined,
                job: backtesting > 0 ? selectedBTJobs : undefined,
                start_time: start,
                end_time: end,
                format: format.key,
                job_created: jobCreated,
            };
            // TODO MC2 handling
            return ReportsService.downloadDataReport('alerts', p, columns, cmc ? cmc.name : undefined)
                .then(res => {
                    let filename = 'alerts';
                    if (format.key === 'csv') {
                        filename += '.csv';
                    }
                    SaveFile(res, filename, format.type);
                });
        };

        // widget API calls
        this.getAlertGroups = ({
            filter,
            start,
            end,
            group,
            limit = 42,
            sort,
            tags,
            tags_op, // eslint-disable-line camelcase
            timeline,
            targets,
        }, controller) => {
            if (!controller) return Promise.resolve();
            const p = {
                sort: this.sortMapping(sort),
                limit,
                json_filter: this.processFilter(filter),
                group: this.groupMapping(group),
                tags: (tags && tags.length > 0) ? tags : undefined,
                tags_op, // eslint-disable-line camelcase
                start_time: start,
                end_time: end,
                target_cmc: targets,
                timeline,
            };
            return FetchAPI({
                method: 'GET',
                url: `${MC2CHECK ? 'mc2/' : ''}widgets/alertgroups/?${ParamsSerializer(p)}`,
                signal: controller.signal,
            });
        };

        this.getAlertTimelineSeries = ({ start, end, series = [] }) => {
            const params = {
                start_time: start,
                end_time: end,
            };
            // deep copy so the GUI isn't impacted
            const _series = JSON.parse(JSON.stringify(series))
                .map(s => ({
                    ...s,
                    json_filter: s.json_filter ? JSON.stringify(this.demap(s.json_filter)) : undefined,
                }));
            return FetchAPI({
                method: 'PUT',
                url: `widgets/alerttimeline/?${ParamsSerializer(params)}`,
                body: _series,
            });
        };

        this.getPolicyAlerts = ({
            start,
            end,
            policy,
            timeline,
        }, controller) => {
            if (!controller) return Promise.resolve();
            const params = {
                start_time: start,
                end_time: end,
                policy,
                timeline,
            };
            return FetchAPI({
                method: 'GET',
                url: `rules/policy/suricata/summary/?${ParamsSerializer(params)}`,
                signal: controller.signal,
            });
        };

        this.mc2GetAlertCounts = ({
            filter,
            start,
            end,
            targets,
            limit = 42,
        }, controller) => {
            if (!controller) return Promise.resolve();
            const params = {
                start_time: start,
                end_time: end,
                json_filter: this.processFilter(filter),
                limit,
                target_cmc: targets,
            };
            return FetchAPI({
                method: 'GET',
                url: `mc2/widgets/alertcounts/?${ParamsSerializer(params)}`,
                signal: controller.signal,
            });
        };

        this.cancelAll = () => {
            this.controller && this.controller.abort();
            this.stopGeofeed();
        };

        // normalizing functions
        this.value = (d, c) => c.getter ? c.getter(d) : _get(d, c.field, '');

        this.count = (d) => { return d ? d.count : undefined };

        this.timestamp = (d) => { return d ? new Date(d.start) : undefined };

        this.end = (d) => { return d ? new Date(d.end) : undefined };

        this.key = d => _groupKey(d);

        // make sure filter data is stringified
        this.sanitize = f => {
            if (!f) return f;

            if (f.nodes) {
                f.nodes = f.nodes.map(this.sanitize);
            }
            // alert json values must be strings
            if (f.value) {
                f.value = `${f.value}`;
            }
            return f;
        };

        // removes at least one unecessary filter level
        // not being used, but may come in handy one day
        this.compress = f => {
            if (!f) return;

            // can we remove a level?
            if (f.nodes &&
                f.nodes.length === 1 &&
                !!f.nodes[0].operator) {
                f = f.nodes[0];
            }

            if (f.nodes) {
                f.nodes = f.nodes.map(this.compress);
            }

            return f;
        };

        this.toEnglish = (f, html, sensorNames, assets, jobs) => FilterToEnglish(
            f,
            { map: defaults, sensorNames: (sensorNames || {}), assets: (assets || {}), jobs: (jobs || {}) },
            html
        );

        return AlertsDataService.instance;
    }
}

// helpers
function _groupKey(d) {
    return (d.groups || [])
        .sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
        .reduce((a, c) => {
            ArrayFromString(c.value).forEach(t => {
                a.push(c.key + ' = ' + ('' + t));
            });
            // add sensor uuid to the group key so it can be filtered on later
            // if (c.key === 'sensor_uuid') a.push('sensor = ' + brcSensorNames.getSensor(c.value).name)
            return a;
        }, [])
        .join(', ');
}

function _aggregate(timeline) {
    const groupso = {};
    [].concat.apply([], (timeline || [])
        .filter(d => d.groups)
        .map(d => d.groups))
        .forEach(g => {
            g.key = _groupKey(g);
            groupso[g.key] = groupso[g.key] || { key: g.key, count: 0, groups: g.groups };
            groupso[g.key].count = groupso[g.key].count + g.count;
        });
    return Object.values(groupso).sort((a, b) => b.count - a.count);
}

const AlertsService = new AlertsDataService();

export {
    AlertsService as default,
    DefaultWidths,
    DefaultMapping,
    AllMapping,
};
